import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from '@risksmart-app/atomic-ui';
import type { ReactElement } from 'react';

import {
  OBJECT_TOOLBAR_OVERFLOW_TEST_ID,
  objectToolbarGroupTestId,
  objectToolbarItemTestId,
} from '../testIds';
import type { ToolbarActionGroup } from '../types';

interface ActionMenuProps {
  /** The trigger element (an `IconButton`), forwarded to `DropdownMenuTrigger`. */
  trigger: ReactElement;
  groups: ToolbarActionGroup[];
}

/**
 * A grouped dropdown of toolbar actions for the overflow (⋮) tray. Renders the
 * non-empty groups in order: each group after the first is preceded by a
 * divider, and a group `label` renders as a section heading.
 */
const ActionMenu = ({ trigger, groups }: ActionMenuProps) => (
  <DropdownMenu>
    <DropdownMenuTrigger render={trigger} />
    <DropdownMenuContent
      align={'end'}
      data-testid={OBJECT_TOOLBAR_OVERFLOW_TEST_ID}
    >
      {groups
        .filter((group) => group.actions.length > 0)
        .map((group, index) => (
          <DropdownMenuGroup
            key={group.id}
            data-testid={objectToolbarGroupTestId(group.id)}
          >
            {index > 0 ? <DropdownMenuSeparator /> : null}
            {group.label ? (
              <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            ) : null}
            {group.actions.map((action) => (
              <DropdownMenuItem
                key={action.id}
                data-testid={objectToolbarItemTestId(action.id)}
                variant={
                  action.variant === 'destructive' ? 'destructive' : 'default'
                }
                disabled={action.disabled}
                onClick={action.onClick}
              >
                {action.iconName ? (
                  <Icon name={action.iconName} size={'sm'} />
                ) : null}
                {action.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

export { ActionMenu };
